import { useCallback, useState } from "react";
import type { PlanEvent, Task, TaskId, EventId } from "../shared/types";
import { useTasks } from "../features/tasks/hooks/use-tasks";
import { useEvents } from "../features/events/hooks/use-events";
import { useTheme } from "../features/settings/hooks/use-theme";
import { useAccent } from "../features/settings/hooks/use-accent";
import { useSearch } from "../features/search/hooks/use-search";
import { useNotifications } from "../shared/hooks/use-notifications";
import { useUndoDelete } from "../shared/hooks/use-undo-delete";
import { importDataSchema } from "../shared/schemas/local.schema";
import { syncImportToDatabase } from "../lib/api/sync";
import { useGamification } from "../features/gamification/hooks/use-gamification";
import { trackLogin } from "../features/gamification/store/gamification-store";
import { track } from "../lib/analytics";

export function useAppData() {
  const {
    activeTasks,
    addTask,
    updateTask,
    deleteTaskLocal,
    confirmDeleteTask,
    cycleStatus,
    addSubtask,
    toggleSubtask,
    deleteSubtask,
    reorderTask,
    importTasks,
    undo,
    redo,
  } = useTasks();

  const {
    events,
    addEvent,
    addEventFull,
    deleteEventLocal,
    confirmDeleteEvent,
    updateEvent,
    importEvents,
    detachOccurrence,
    addRecurrenceException,
    resyncOccurrence,
    resyncAllOccurrences,
    undoEvent,
    redoEvent,
  } = useEvents();

  const taskUndoDelete = useUndoDelete<Task>();
  const eventUndoDelete = useUndoDelete<unknown>();
  const [pendingRecurrenceDelete, setPendingRecurrenceDelete] = useState<PlanEvent | null>(null);

  const deleteTask = useCallback(
    (id: TaskId) => {
      const task = activeTasks.find((t) => t.id === id);
      if (!task) return;
      track("task_deleted", {});
      taskUndoDelete.requestDelete(
        task,
        "Tâche supprimée",
        () => deleteTaskLocal(id),
        () => undo(),
        () => confirmDeleteTask(id),
      );
    },
    [activeTasks, taskUndoDelete, deleteTaskLocal, undo, confirmDeleteTask],
  );

  const extractBaseId = useCallback((instanceId: string, date: string): EventId => {
    const suffix = `_${date}`;
    if (instanceId.endsWith(suffix))
      return instanceId.slice(0, -suffix.length) as EventId;
    return instanceId as EventId;
  }, []);

  const deleteEventDirect_noRecurrence = useCallback(
    (id: EventId) => {
      track("event_deleted", {});
      eventUndoDelete.requestDelete(
        { id },
        "Événement supprimé",
        () => deleteEventLocal(id),
        () => undoEvent(),
        () => confirmDeleteEvent(id),
      );
    },
    [eventUndoDelete, deleteEventLocal, undoEvent, confirmDeleteEvent],
  );

  const deleteEvent = useCallback(
    (id: EventId, expandedEvent?: PlanEvent, skipRecurrenceCheck?: boolean) => {
      const rawEvent = events.find((e) => e.id === id);
      const event = rawEvent ?? expandedEvent;
      if (!event) return;

      if (!skipRecurrenceCheck && event.recurrence?.type) {
        setPendingRecurrenceDelete(event);
      } else {
        track("event_deleted", {});
        eventUndoDelete.requestDelete(
          event,
          "Événement supprimé",
          () => deleteEventLocal(id),
          () => undoEvent(),
          () => confirmDeleteEvent(id),
        );
      }
    },
    [events, eventUndoDelete, deleteEventLocal, undoEvent, confirmDeleteEvent],
  );

  const handleRecurrenceDeleteChoice = useCallback(
    (action: "this" | "all") => {
      if (!pendingRecurrenceDelete) return;
      const baseId = extractBaseId(
        pendingRecurrenceDelete.id as string,
        pendingRecurrenceDelete.date,
      );
      if (action === "this") {
        addRecurrenceException(baseId, pendingRecurrenceDelete.date);
      } else {
        deleteEventDirect_noRecurrence(baseId);
      }
      setPendingRecurrenceDelete(null);
    },
    [pendingRecurrenceDelete, extractBaseId, addRecurrenceException, deleteEventDirect_noRecurrence],
  );

  const { theme, setTheme } = useTheme();
  const { accent, setAccent } = useAccent();
  const search = useSearch(activeTasks, events);
  const { permissionGranted, requestPermission } = useNotifications(events, activeTasks);
  const gamification = useGamification();
  trackLogin();

  const handleImport = useCallback(
    (raw: { tasks: unknown[]; events: unknown[]; habits?: unknown[] }) => {
      const parsed = importDataSchema.safeParse(raw);
      if (!parsed.success) return;
      importTasks(parsed.data.tasks);
      importEvents(parsed.data.events);
      void syncImportToDatabase(
        parsed.data.tasks,
        parsed.data.events,
        parsed.data.habits,
      );
      track("data_imported", {
        task_count: parsed.data.tasks.length,
        event_count: parsed.data.events.length,
      });
    },
    [importTasks, importEvents],
  );

  return {
    activeTasks,
    addTask,
    updateTask,
    deleteTask,
    cycleStatus,
    addSubtask,
    toggleSubtask,
    deleteSubtask,
    reorderTask,
    events,
    addEvent,
    addEventFull,
    deleteEvent,
    updateEvent,
    detachOccurrence,
    addRecurrenceException,
    resyncOccurrence,
    resyncAllOccurrences,
    undoEvent,
    redoEvent,
    undo,
    redo,
    taskUndoDelete,
    eventUndoDelete,
    pendingRecurrenceDelete,
    setPendingRecurrenceDelete,
    handleRecurrenceDeleteChoice,
    theme,
    setTheme,
    accent,
    setAccent,
    search,
    handleImport,
    gamification,
    notificationPermission: permissionGranted,
    requestNotificationPermission: requestPermission,
  };
}
